import logger from '@/utils/logger';
/**
 * RTC 错误处理
 */

const isPeerConnectionError = (error: any): boolean => {
  if (!error) return false;
  const message = String(error.message || error).toLowerCase();
  const stack = String(error.stack || '').toLowerCase();
  const patterns = [
    'connectionstate',
    'peerconnection',
    'peer connection',
    'webrtc connection',
    'rtc peer connection',
    "cannot read properties of undefined (reading 'connectionstate')",
  ];
  return patterns.some((p) => message.includes(p) || stack.includes(p));
};

const handlePeerConnectionError = (error: any, context?: string): void => {
  logger.warn(`[RTC] PeerConnection错误 (${context || 'unknown'})`, {
    message: (error as any)?.message,
    stack: (error as any)?.stack,
    timestamp: new Date().toISOString(),
  });
};

const safeRTCOperation = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  fallback?: T
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (isPeerConnectionError(error)) {
      handlePeerConnectionError(error, operationName);
      // 简单的一次性重试
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
      try {
        return await operation();
      } catch (retryError) {
        handlePeerConnectionError(retryError, `${operationName}_retry`);
        if (fallback !== undefined) return fallback as T;
        if (!(retryError instanceof Error)) throw new Error(JSON.stringify(retryError));
        throw retryError;
      }
    }
    if (!(error instanceof Error)) throw new Error(JSON.stringify(error));
    throw error;
  }
};

const isEngineReady = (engine: any): boolean => {
  try {
    return !!(engine && typeof engine === 'object' && engine !== null);
  } catch {
    return false;
  }
};

const getConnectionStateSafely = (engine: any): string => {
  try {
    if (!isEngineReady(engine)) return 'disconnected';
    if (typeof (engine as any).getConnectionState === 'function') {
      return (engine as any).getConnectionState();
    }
    return 'disconnected';
  } catch (error) {
    handlePeerConnectionError(error, 'getConnectionState');
    return 'disconnected';
  }
};

export const rtcErrorHandler = {
  isPeerConnectionError,
  handlePeerConnectionError,
  safeRTCOperation,
  isEngineReady,
  getConnectionStateSafely,
};
